import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Accordion,
  AccordionItem,
  ActionButtons,
  Button,
  DataTable,
  LinkButton,
  Modal,
  Search,
  Tabs,
  type DataTableColumn,
  type DataTableRow,
} from '@enamelai/design-system'
import { Icon as EditIcon } from '@enamelai/design-system/icons/Edit'
import './ChartingModals.css'
import ApptToothModal from '@components/layout/Modals/Appointment/ApptTooth.modal'
import AddCommonProcedureModal from '@components/layout/Modals/Appointment/AddCommonProcedure.modal'
import AddEditBundled from '@components/pages/Settings/Clinical/BundledFavorite/AddEditBundled.component'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  ICommonProcedure,
  IToothDetails,
  IInsurancePlan,
} from '@dataTypes/appointment.type'
import {
  IPhaseItems,
  IProcedureDentalCondition,
} from '@dataTypes/treatmentPlan.types'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchAllProcedureSettings } from '@store/actions'
import { setTxPlanProcedureCatalog } from '@store/reducers/charting.reducer'
import { BundledProcedure } from '@dataTypes/bundledProcedure.type'
import { ProcedureSettingsType } from '@dataTypes/procedureSettings.type'
import { InsurancePlan } from '@dataTypes/insurance.types'
import {
  addBundledProcedure,
  addCommonProcedure as createCommonProcedureApi,
  checkBundledCodeIfExists,
  getBundledProcedures,
  getCommonProc,
} from '@services/api/procedure.api'
import { getInsuranceFieldValues } from '@utils/insurance.util'
import { resolveProcedureSurfaceSelectionRules } from '@utils/procedure-surface-selection.utils'
import {
  computeTxPlanProcedureSearchResults,
  filterVisibleBundledProcedures,
  filterVisibleCommonProcedures,
  formatBundleDentalConditionNames,
  formatBundleSearchLabel,
  formatDentalConditionNames,
  resolveBundleToProcedureSettings,
  resolveCommonProcedureToProcedureSettings,
} from '@utils/tx-plan-procedure-search.utils'
import {
  convertToothDetailsToToothSurfaces,
  formatPresetToothNumsLabel,
  formatToothDetailsToSurfaceString,
  splitToothDetailsByTooth,
} from '@utils/tx-plan-tooth-details.utils'
import { logger } from '@utils/safeLogger'
interface AddTxPlanProcedureModalProps {
  show: boolean
  onHide: () => void
  onSelectProcedure: (procedure: IPhaseItems) => void
  selectedInsurance: InsurancePlan | null
  discountPercent: number
  insuranceList: InsurancePlan[]
  patientInsurancePlans?: IInsurancePlan[]
  presetToothDetails?: IToothDetails[]
  initialQuery?: string
}

type SelectedProcedureItem = ProcedureSettingsType & {
  toothDetails?: IToothDetails[]
}

type BundledFormData = {
  bundled_name: string
  bundled_code: string
  procedures: Array<{ procedure_id: string; procedure_code: string }>
}

type SelectedProcedureRow = DataTableRow & {
  id: string
  code: string
  description: string
  itemIndex: number
}

const FAVORITES_BUNDLES_TABS = [
  { id: 'favourites', label: 'Favourites' },
  { id: 'bundles', label: 'Bundles' },
]

const cloneToothDetails = (details: IToothDetails[]): IToothDetails[] =>
  details.map((detail) => ({
    ...detail,
    surf: detail.surf ? [...detail.surf] : detail.surf,
  }))

const normalizeProcedureCode = (value: string | null | undefined): string =>
  value?.replace(/\s+/g, '').toLowerCase() ?? ''

const AddTxPlanProcedureModal: React.FC<AddTxPlanProcedureModalProps> = ({
  show,
  onHide,
  onSelectProcedure,
  selectedInsurance,
  discountPercent,
  insuranceList,
  patientInsurancePlans,
  presetToothDetails,
  initialQuery,
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const procedureSettings = useAppSelector(
    (state) => state.charting.procedureSettings,
  )
  const bundledProcedures = useAppSelector(
    (state) => state.charting.bundledProcedures,
  )
  const commonProcedures = useAppSelector(
    (state) => state.charting.commonProcedures,
  )
  const procedureCatalogFetched = useAppSelector(
    (state) => state.charting.txPlanProcedureCatalogFetched,
  )
  const [query, setQuery] = useState('')
  const [bundleResolveError, setBundleResolveError] = useState<string | null>(
    null,
  )
  const [selectedItems, setSelectedItems] = useState<SelectedProcedureItem[]>(
    [],
  )
  const [showRegionModal, setShowRegionModal] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [showAddFavoriteModal, setShowAddFavoriteModal] = useState(false)
  const [showCreateBundleModal, setShowCreateBundleModal] = useState(false)
  const [isSavingCatalogItem, setIsSavingCatalogItem] = useState(false)
  const [favoritesBundlesTab, setFavoritesBundlesTab] = useState<
    'favourites' | 'bundles'
  >('favourites')
  const catalogFetchStartedRef = useRef(false)
  const isProcedureCatalogLoaded =
    procedureCatalogFetched.settings &&
    procedureCatalogFetched.bundled &&
    procedureCatalogFetched.common
  const searchResults = useMemo(
    () =>
      computeTxPlanProcedureSearchResults(
        procedureSettings,
        bundledProcedures,
        query,
      ),
    [procedureSettings, bundledProcedures, query],
  )
  const hasSearchResults =
    searchResults.procedures.length > 0 || searchResults.bundles.length > 0
  const visibleCommonProcedures = useMemo(
    () => filterVisibleCommonProcedures(commonProcedures),
    [commonProcedures],
  )
  const visibleBundledProcedures = useMemo(
    () => filterVisibleBundledProcedures(bundledProcedures),
    [bundledProcedures],
  )
  const selectedProcedureCodes = useMemo(
    () => new Set(selectedItems.map((item) => item.procedure.procedure_code)),
    [selectedItems],
  )
  const activeInitialToothSelections = useMemo((): IToothDetails[] => {
    if (activeIndex === null) {
      return []
    }
    return selectedItems[activeIndex]?.toothDetails ?? []
  }, [activeIndex, selectedItems])
  const activeProcedureSurfaceSelection = useMemo(() => {
    if (activeIndex === null) {
      return null
    }
    const item = selectedItems[activeIndex]
    if (!item) {
      return null
    }
    return resolveProcedureSurfaceSelectionRules(
      item.procedure?.procedure_dental_conditions,
      item.procedure?.description ?? item.description ?? null,
    )
  }, [activeIndex, selectedItems])
  const presetToothLabel = useMemo((): string => {
    const toothNums = (presetToothDetails ?? [])
      .map((detail) => detail.tooth_num)
      .filter((toothNum): toothNum is number => toothNum !== undefined)
    return formatPresetToothNumsLabel(toothNums)
  }, [presetToothDetails])

  const buildNewSelectedItem = useCallback(
    (item: ProcedureSettingsType): SelectedProcedureItem => {
      if (presetToothDetails && presetToothDetails.length > 0) {
        const toothDetails = cloneToothDetails(presetToothDetails)
        return {
          ...item,
          surface: formatToothDetailsToSurfaceString(toothDetails),
          toothDetails,
        }
      }
      return { ...item, surface: '', toothDetails: [] }
    },
    [presetToothDetails],
  )

  const refreshFavoriteCatalog = useCallback(async (): Promise<boolean> => {
    const response = await getCommonProc()
    if (response.statusCode === 200) {
      dispatch(
        setTxPlanProcedureCatalog({
          commonProcedures: response.data?.common_procedures ?? [],
        }),
      )
      return true
    }
    return false
  }, [dispatch])

  const refreshBundleCatalog = useCallback(async (): Promise<boolean> => {
    const response = await getBundledProcedures()
    if (response.statusCode === 200) {
      dispatch(
        setTxPlanProcedureCatalog({
          bundledProcedures: response.data?.bundled_procedures ?? [],
        }),
      )
      return true
    }
    return false
  }, [dispatch])

  const saveFavoriteProcedure = useCallback(
    async (procId: string): Promise<void> => {
      setIsSavingCatalogItem(true)
      try {
        const response = await createCommonProcedureApi({
          procedure_id: procId,
        })
        if (response.statusCode === 200 || response.statusCode === 201) {
          const refreshed = await refreshFavoriteCatalog()
          if (refreshed) {
            showToast(
              createSuccessToast(
                'Favorite procedure added successfully',
                'Favorite procedure has been added successfully.',
              ),
            )
            setShowAddFavoriteModal(false)
          } else {
            showToast(
              createFailedToast(
                'Favorite procedure added',
                'Favorite was saved but the list could not be refreshed. Reopen this modal to see it.',
              ),
            )
            setShowAddFavoriteModal(false)
          }
        } else {
          showToast(
            createFailedToast(
              'Favorite procedure adding failed',
              'Favorite procedure adding failed. Please try again later.',
            ),
          )
        }
      } catch (error) {
        logger.error('Error adding favorite procedure:', error)
        showToast(
          createFailedToast(
            'Favorite procedure save failed',
            'Please try again later.',
          ),
        )
      } finally {
        setIsSavingCatalogItem(false)
      }
    },
    [refreshFavoriteCatalog, showToast],
  )

  const bundledActions = useMemo(
    () => ({
      save: async (
        _idToEdit: string | null,
        formData: BundledFormData,
      ): Promise<void> => {
        setIsSavingCatalogItem(true)
        try {
          const bundledCodeExists = await checkBundledCodeIfExists(
            formData.bundled_code,
          )
          if (bundledCodeExists?.data?.result?.exists) {
            showToast(
              createFailedToast(
                'Bundle code already exists',
                'Bundle code already exists. Please try again with a different bundle code.',
              ),
            )
            return
          }
          const response = await addBundledProcedure(formData)
          if (response.statusCode === 200) {
            const refreshed = await refreshBundleCatalog()
            if (refreshed) {
              showToast(
                createSuccessToast(
                  'Bundled procedure added successfully',
                  'Bundled procedure has been added successfully.',
                ),
              )
              setShowCreateBundleModal(false)
            } else {
              showToast(
                createFailedToast(
                  'Bundled procedure added',
                  'Bundle was saved but the list could not be refreshed. Reopen this modal to see it.',
                ),
              )
              setShowCreateBundleModal(false)
            }
          } else {
            showToast(
              createFailedToast(
                'Bundled procedure adding failed',
                'Bundled procedure adding failed. Please try again later.',
              ),
            )
          }
        } catch (error) {
          logger.error('Error adding bundled procedure:', error)
          showToast(
            createFailedToast(
              'Bundled procedure save failed',
              'Please try again later.',
            ),
          )
        } finally {
          setIsSavingCatalogItem(false)
        }
      },
    }),
    [refreshBundleCatalog, showToast],
  )

  useEffect(() => {
    if (!show) {
      catalogFetchStartedRef.current = false
      setShowAddFavoriteModal(false)
      setShowCreateBundleModal(false)
      setFavoritesBundlesTab('favourites')
      return
    }
    setBundleResolveError(null)
    setSelectedItems([])
    setActiveIndex(null)
    if (isProcedureCatalogLoaded || catalogFetchStartedRef.current) {
      return
    }
    catalogFetchStartedRef.current = true
    const needsProcedureSettings = !procedureCatalogFetched.settings
    const needsBundledProcedures = !procedureCatalogFetched.bundled
    const needsCommonProcedures = !procedureCatalogFetched.common
    const loadModalData = async (): Promise<void> => {
      try {
        setIsLoading(true)
        await Promise.all([
          needsProcedureSettings
            ? dispatch(fetchAllProcedureSettings(null)).unwrap()
            : Promise.resolve(),
          needsBundledProcedures
            ? getBundledProcedures().then((bundledResponse) => {
                dispatch(
                  setTxPlanProcedureCatalog({
                    bundledProcedures:
                      bundledResponse.statusCode === 200
                        ? (bundledResponse.data?.bundled_procedures ?? [])
                        : [],
                  }),
                )
              })
            : Promise.resolve(),
          needsCommonProcedures
            ? getCommonProc().then((commonResponse) => {
                dispatch(
                  setTxPlanProcedureCatalog({
                    commonProcedures:
                      commonResponse.statusCode === 200
                        ? (commonResponse.data?.common_procedures ?? [])
                        : [],
                  }),
                )
              })
            : Promise.resolve(),
        ])
      } catch (error) {
        logger.error('Failed to fetch procedures:', error)
        dispatch(
          setTxPlanProcedureCatalog({
            ...(needsBundledProcedures ? { bundledProcedures: [] } : {}),
            ...(needsCommonProcedures ? { commonProcedures: [] } : {}),
          }),
        )
      } finally {
        setIsLoading(false)
      }
    }
    void loadModalData()
  }, [show, dispatch, isProcedureCatalogLoaded, procedureCatalogFetched])

  useEffect(() => {
    if (!show) return
    setQuery(initialQuery ?? '')
  }, [show, initialQuery])

  const handleSearch = (value: string): void => {
    setQuery(value)
    setBundleResolveError(null)
  }

  const handleClearSearch = (): void => {
    setQuery('')
    setBundleResolveError(null)
  }

  const addProcedure = (item: ProcedureSettingsType): void => {
    if (
      !selectedItems.find(
        (i) => i.procedure.procedure_code === item.procedure.procedure_code,
      )
    ) {
      setSelectedItems((prev) => [...prev, buildNewSelectedItem(item)])
      setQuery('')
      setBundleResolveError(null)
    }
  }

  const addCommonProcedure = (commonProcedure: ICommonProcedure): void => {
    if (
      selectedProcedureCodes.has(
        commonProcedure.procedure?.procedure_code ?? '',
      )
    ) {
      return
    }
    const resolved = resolveCommonProcedureToProcedureSettings(
      commonProcedure,
      procedureSettings,
    )
    if (!resolved) {
      setBundleResolveError(
        'Favorite procedure is not configured in procedure settings.',
      )
      return
    }
    addProcedure(resolved)
  }

  const addBundle = (bundle: BundledProcedure): void => {
    const resolved = resolveBundleToProcedureSettings(bundle, procedureSettings)
    if (resolved.length === 0) {
      setBundleResolveError(
        'Bundle procedures are not configured in procedure settings.',
      )
      return
    }
    const selectedCodes = new Set(
      selectedItems.map((i) => i.procedure.procedure_code),
    )
    const toAdd = resolved.filter(
      (item) => !selectedCodes.has(item.procedure.procedure_code),
    )
    if (toAdd.length === 0) {
      setQuery('')
      setBundleResolveError(null)
      return
    }
    setSelectedItems((prev) => [
      ...prev,
      ...toAdd.map((item) => buildNewSelectedItem(item)),
    ])
    setQuery('')
    setBundleResolveError(null)
  }

  const openRegionModal = (index: number): void => {
    setActiveIndex(index)
    setShowRegionModal(true)
  }

  const removeSelectedProcedure = (index: number): void => {
    if (activeIndex === index) {
      setShowRegionModal(false)
      setActiveIndex(null)
    } else if (activeIndex !== null && activeIndex > index) {
      setActiveIndex(activeIndex - 1)
    }
    setSelectedItems((prev) => prev.filter((_, idx) => idx !== index))
  }

  const handleSaveRegion = (selections: IToothDetails[]): void => {
    if (activeIndex !== null) {
      const updated = [...selectedItems]
      // Store the tooth details for later conversion to tooth_surfaces
      updated[activeIndex].toothDetails = selections
      updated[activeIndex].surface =
        formatToothDetailsToSurfaceString(selections)
      setSelectedItems(updated)
      setActiveIndex(null)
    }
    setShowRegionModal(false)
  }

  const submitSelectedItems = useCallback(
    (itemsToSubmit: SelectedProcedureItem[]): void => {
      if (itemsToSubmit.length === 0) {
        return
      }

      itemsToSubmit.forEach((item) => {
        const clinicFeeRaw =
          item.procedure?.procedure_settings?.clinic_fee ?? item.clinic_fee ?? 0

        type InsurancePlanWithBenefits = (InsurancePlan | IInsurancePlan) & {
          benefits?: Array<{ procedure_id: string; co_insurance: number }>
        }
        type InsurancePlanWithServices = InsurancePlan & {
          services?: Array<{
            benefits?: Array<{ procedure_id: string; co_insurance: number }>
          }>
          benefits?: Array<{ procedure_id: string; co_insurance: number }>
        }

        let insurancePlansForCalculation: InsurancePlanWithBenefits[] = []
        if (patientInsurancePlans && patientInsurancePlans.length > 0) {
          insurancePlansForCalculation = patientInsurancePlans.map(
            (plan) =>
              ({
                ...plan,
                benefits: plan.benefits || [],
              }) as InsurancePlanWithBenefits,
          )
        } else if (insuranceList.length > 0) {
          // Transform InsurancePlan to IInsurancePlan format by extracting benefits from services
          insurancePlansForCalculation = insuranceList.map((plan) => {
            const planWithServices = plan as InsurancePlanWithServices
            // Extract benefits from services if available
            if (planWithServices.services && !planWithServices.benefits) {
              const allBenefits: Array<{
                procedure_id: string
                co_insurance: number
              }> = []
              const services = planWithServices.services
              services.forEach((service) => {
                if (service.benefits) {
                  allBenefits.push(...service.benefits)
                }
              })
              if (allBenefits.length > 0) {
                return {
                  ...plan,
                  benefits: allBenefits,
                } as InsurancePlanWithBenefits
              }
            }
            return plan
          })
        } else {
          insurancePlansForCalculation = insuranceList
        }

        const insuranceValues = getInsuranceFieldValues(
          item.procedure.procedure_id,
          clinicFeeRaw as number,
          selectedInsurance?.insurance_plan_id ?? '',
          insurancePlansForCalculation as unknown as InsurancePlan[],
        )

        const clinicFee = Number(
          insuranceValues.clinic_fee ?? clinicFeeRaw ?? 0,
        )
        const allowedAmount = Number(insuranceValues.allowed_amount ?? 0)
        const writeOff = Number(insuranceValues.write_off ?? 0)
        const insuranceAmt = Number(
          insuranceValues.estimated_insurance_pay ?? 0,
        )
        const estimatedPatientResp = Number(
          insuranceValues.estimated_patient_resp ?? 0,
        )
        const adjustmentAmt = (estimatedPatientResp * discountPercent) / 100

        // Calculate outstanding (after discount)
        const outstandingAmt = estimatedPatientResp - adjustmentAmt

        const resolvedProcedureDentalConditions =
          item.procedure?.procedure_dental_conditions?.filter(
            (mapping: IProcedureDentalCondition) => mapping.dental_condition,
          )

        const toothDetailGroups = splitToothDetailsByTooth(
          item.toothDetails ?? [],
        )

        toothDetailGroups.forEach((toothDetails) => {
          const surfaceString = formatToothDetailsToSurfaceString(toothDetails)
          const toothSurfaces = convertToothDetailsToToothSurfaces(
            toothDetails,
            '',
          )
          const procedureItem: IPhaseItems = {
            item_id: '',
            phase_id: null,
            procedure_id: item.procedure.procedure_id,
            surface: surfaceString || null,
            tooth_surfaces:
              toothSurfaces.length > 0 ? toothSurfaces : undefined,
            proceduredentalcondition:
              resolvedProcedureDentalConditions &&
              resolvedProcedureDentalConditions.length > 0
                ? resolvedProcedureDentalConditions
                : undefined,
            charges: clinicFee,
            insurance: insuranceAmt,
            adjustments: adjustmentAmt,
            outstanding: outstandingAmt,
            allowed_amount: allowedAmount,
            write_off: writeOff,
            procedure_settings: item,
            is_hidden: false,
          }
          onSelectProcedure(procedureItem)
        })
      })
      onHide()
    },
    [
      discountPercent,
      insuranceList,
      onHide,
      onSelectProcedure,
      patientInsurancePlans,
      selectedInsurance,
    ],
  )

  const handleSubmit = useCallback((): void => {
    submitSelectedItems(selectedItems)
  }, [selectedItems, submitSelectedItems])

  const selectedProcedureRows = useMemo(
    (): SelectedProcedureRow[] =>
      selectedItems.map((item, index) => ({
        id: `${item.procedure.procedure_id}_${index}`,
        code: item.procedure.procedure_code,
        description: item.procedure.description,
        itemIndex: index,
      })),
    [selectedItems],
  )

  const selectedProcedureColumns = useMemo(
    (): DataTableColumn<SelectedProcedureRow>[] => [
      {
        id: 'code',
        label: 'Code',
        width: '16%',
      },
      {
        id: 'description',
        label: 'Description',
        width: '42%',
        renderCell: (row): React.ReactNode => (
          <span
            className="add-tx-plan-procedure-description"
            title={String(row.description ?? '')}
          >
            {row.description}
          </span>
        ),
      },
      {
        id: 'tooth',
        label: 'Tooth/Surface',
        width: '34%',
        renderCell: (row): React.ReactNode => {
          const item = selectedItems[row.itemIndex]
          if (!item) {
            return null
          }
          if (item.surface) {
            return (
              <div className="d-flex align-items-center gap-2">
                <span>{item.surface}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-label={`Edit region for ${item.procedure.procedure_code}`}
                  title="Edit region"
                  leftIcon={<EditIcon width={16} height={16} aria-hidden />}
                  onClick={(): void => openRegionModal(row.itemIndex)}
                />
              </div>
            )
          }
          return (
            <LinkButton
              type="button"
              size="sm"
              underline={false}
              className="add-region-link"
              onClick={(): void => openRegionModal(row.itemIndex)}
            >
              Add region
            </LinkButton>
          )
        },
      },
    ],
    [selectedItems],
  )

  useEffect(() => {
    if (!show || isLoading || !initialQuery) return

    const normalizedQuery = normalizeProcedureCode(initialQuery)
    if (!normalizedQuery) return

    const exactMatch = procedureSettings.find(
      (item) =>
        normalizeProcedureCode(item.procedure?.procedure_code) ===
        normalizedQuery,
    )
    if (!exactMatch) return

    const alreadySelected = selectedItems.some(
      (item) =>
        normalizeProcedureCode(item.procedure?.procedure_code) ===
        normalizedQuery,
    )
    const itemsToSubmit = alreadySelected
      ? selectedItems
      : [...selectedItems, buildNewSelectedItem(exactMatch)]
    submitSelectedItems(itemsToSubmit)
  }, [
    buildNewSelectedItem,
    initialQuery,
    isLoading,
    procedureSettings,
    selectedItems,
    show,
    submitSelectedItems,
  ])

  return (
    <>
      <Modal
        open={show}
        onClose={onHide}
        size="lg"
        className="add-tx-plan-procedure-modal"
        title={
          presetToothLabel
            ? `Add procedures (${presetToothLabel})`
            : 'Add procedures'
        }
        primaryLabel="Add to Plan"
        secondaryLabel="Cancel"
        primaryDisabled={selectedItems.length === 0 || isLoading}
        onPrimaryClick={handleSubmit}
        onSecondaryClick={onHide}
      >
        {presetToothLabel && (
          <div className="add-tx-plan-preset-tooth-banner mb-3">
            Procedures added here will default to{' '}
            {presetToothLabel.toLowerCase()}. Edit surfaces per procedure when
            required.
          </div>
        )}

        {!isLoading && (
          <Accordion className="add-tx-plan-favorites-bundles">
            <AccordionItem id="favorites-bundles" title="Favourites & Bundles">
              <div className="add-tx-plan-favorites-bundles-nav">
                <Tabs
                  className="add-tx-plan-favorites-bundles-tabs"
                  items={FAVORITES_BUNDLES_TABS}
                  value={favoritesBundlesTab}
                  onChange={(tabId): void => {
                    if (tabId === 'favourites' || tabId === 'bundles') {
                      setFavoritesBundlesTab(tabId)
                    }
                  }}
                />
                <Button
                  variant="primary"
                  size="sm"
                  className="add-tx-plan-favorites-bundles-nav-action"
                  onClick={() => {
                    if (favoritesBundlesTab === 'favourites') {
                      setShowAddFavoriteModal(true)
                    } else {
                      setShowCreateBundleModal(true)
                    }
                  }}
                  disabled={isSavingCatalogItem}
                >
                  {favoritesBundlesTab === 'favourites'
                    ? 'Add new favorite'
                    : 'Create bundle'}
                </Button>
              </div>
              {favoritesBundlesTab === 'favourites' ? (
                <div className="add-tx-plan-procedure-grid">
                  {visibleCommonProcedures.map((item) => {
                    const isSelected = selectedProcedureCodes.has(
                      item.procedure?.procedure_code ?? '',
                    )
                    return (
                      <div
                        key={item.common_proc_id}
                        className={`add-tx-plan-procedure-card${
                          isSelected
                            ? ' add-tx-plan-procedure-card--selected'
                            : ''
                        }`}
                        role="button"
                        tabIndex={isSelected ? -1 : 0}
                        onClick={() => addCommonProcedure(item)}
                        onKeyDown={(event) => {
                          if (
                            !isSelected &&
                            (event.key === 'Enter' || event.key === ' ')
                          ) {
                            event.preventDefault()
                            addCommonProcedure(item)
                          }
                        }}
                      >
                        <div className="add-tx-plan-procedure-card-description">
                          {item.procedure?.description ?? ''}
                        </div>
                        <div className="add-tx-plan-procedure-card-details">
                          {item.procedure?.procedure_code ?? ''}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="add-tx-plan-procedure-grid">
                  {visibleBundledProcedures.map((bundle) => (
                    <div
                      key={bundle.bundled_procedure_id}
                      className="add-tx-plan-procedure-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => addBundle(bundle)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          addBundle(bundle)
                        }
                      }}
                    >
                      <div className="add-tx-plan-procedure-card-description">
                        {bundle.bundled_name?.trim() || bundle.bundled_code}
                      </div>
                      <div className="add-tx-plan-procedure-card-details">
                        {bundle.bundled_code}
                        {(bundle.bundled_procedure_items ?? []).length > 0 &&
                          ` · ${(bundle.bundled_procedure_items ?? [])
                            .map((item) => item.procedure?.procedure_code ?? '')
                            .filter(Boolean)
                            .join(', ')}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </AccordionItem>
          </Accordion>
        )}

        <div className="mb-3 add-tx-plan-search">
          <span className="add-tx-plan-search-label">Search procedure</span>
          <Search
            ariaLabel="Search procedure"
            placeholder="e.g. Amalgam, Resin..."
            value={query}
            disabled={isLoading}
            onChange={handleSearch}
            onClear={handleClearSearch}
          />
        </div>

        {isLoading && (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading procedures...</span>
          </div>
        )}

        {!isLoading && bundleResolveError && (
          <div className="text-center py-2 text-danger small mb-2">
            {bundleResolveError}
          </div>
        )}

        {!isLoading && query && hasSearchResults && (
          <div className="border rounded p-2 mb-3 add-tx-plan-search-results">
            {searchResults.procedures.length > 0 && (
              <>
                <div className="add-tx-plan-search-section-label">
                  Procedures
                </div>
                {searchResults.procedures.map((item) => {
                  const conditionLabel = formatDentalConditionNames(
                    item.procedure?.procedure_dental_conditions,
                  )
                  return (
                    <div
                      key={item.procedure.procedure_id}
                      style={{ cursor: 'pointer' }}
                      className="mb-1 add-tx-plan-filter-text add-tx-plan-filter-text--result"
                      onClick={() => addProcedure(item)}
                    >
                      <span className="add-tx-plan-filter-text-main">
                        {item.procedure.procedure_code} —{' '}
                        {item.procedure.description}
                      </span>
                      {conditionLabel ? (
                        <span
                          className="add-tx-plan-filter-text-condition"
                          title={conditionLabel}
                        >
                          {conditionLabel}
                        </span>
                      ) : null}
                    </div>
                  )
                })}
              </>
            )}
            {searchResults.bundles.length > 0 && (
              <>
                <div className="add-tx-plan-search-section-label">Bundles</div>
                {searchResults.bundles.map((bundle) => {
                  const conditionLabel =
                    formatBundleDentalConditionNames(bundle)
                  return (
                    <div
                      key={bundle.bundled_procedure_id}
                      style={{ cursor: 'pointer' }}
                      className="mb-1 add-tx-plan-filter-text add-tx-plan-filter-text--result"
                      onClick={() => addBundle(bundle)}
                    >
                      <span className="add-tx-plan-filter-text--bundle">
                        <span className="add-tx-plan-bundle-chip">Bundle</span>
                        <span className="add-tx-plan-filter-text-main">
                          {formatBundleSearchLabel(bundle)}
                        </span>
                      </span>
                      {conditionLabel ? (
                        <span
                          className="add-tx-plan-filter-text-condition"
                          title={conditionLabel}
                        >
                          {conditionLabel}
                        </span>
                      ) : null}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        )}

        {!isLoading && query && !hasSearchResults && (
          <div className="text-center py-2 text-muted">
            No procedures or bundles found matching &quot;{query}&quot;
          </div>
        )}

        {selectedItems.length > 0 && (
          <div className="add-tx-plan-procedure-table">
            <DataTable<SelectedProcedureRow>
              columns={selectedProcedureColumns}
              rows={selectedProcedureRows}
              actionsLabel="Actions"
              renderActions={(row): React.ReactNode => {
                const item = selectedItems[row.itemIndex]
                if (!item) {
                  return null
                }
                return (
                  <ActionButtons
                    size="sm"
                    showView={false}
                    showEdit={false}
                    onDelete={(): void => {
                      removeSelectedProcedure(row.itemIndex)
                    }}
                  />
                )
              }}
            />
          </div>
        )}
      </Modal>

      <ApptToothModal
        isOpen={showRegionModal}
        closeModal={() => setShowRegionModal(false)}
        saveToothSelection={handleSaveRegion}
        skipToothSelection={() => {}}
        secondaryActionLabel="Cancel"
        initialToothSelections={activeInitialToothSelections}
        procedureSurfaceSelection={activeProcedureSurfaceSelection}
        procedureSurfaceFilterText={
          activeIndex !== null && selectedItems[activeIndex]
            ? (selectedItems[activeIndex].procedure?.description ??
              selectedItems[activeIndex].description ??
              null)
            : null
        }
      />
      <AddCommonProcedureModal
        key={showAddFavoriteModal ? 'open' : 'closed'}
        isOpen={showAddFavoriteModal}
        closeModal={() => setShowAddFavoriteModal(false)}
        commonProcedures={commonProcedures}
        saveCommonProcedure={(procId) => {
          void saveFavoriteProcedure(procId)
        }}
        isSaving={isSavingCatalogItem}
      />
      <AddEditBundled
        key={showCreateBundleModal ? 'open' : 'closed'}
        show={showCreateBundleModal}
        onHide={() => setShowCreateBundleModal(false)}
        actions={bundledActions}
        recordToEdit={null}
        nestedInModal
      />
    </>
  )
}

export default AddTxPlanProcedureModal
